import { useMutation } from '@tanstack/react-query';

import type { NotifyCandidateMetadata } from 'clients/CampaignsClient/CampaignsClient.types';
import type { IndeedJobContact } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';
import type { ContactCandidateData } from '../useContactCandidateMutation.types';

import { useIndeedReplyMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedReplyMessageMutation';
import { useIndeedSendMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedSendMessageMutation';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { extractEsPersonId } from '../utils/extractEsPersonId';

export const useContactIndeedMutation = () => {
  const { data: currentUser } = useCurrentUserQuery();

  const indeedReplyMessageMutation = useIndeedReplyMessageMutation();
  const indeedSendMessageMutation = useIndeedSendMessageMutation();
  const { isFeatureToggleEnabled } = useFeatureToggles();

  return useMutation(async (params: ContactCandidateData<'indeed'>) => {
    if (!currentUser) {
      throw new Error('Current user not found');
    }

    let thread_id = undefined;
    let metadata: NotifyCandidateMetadata | undefined = undefined;
    const {
      esPersonId,
      subject,
      message,
      indeedProjectId,
      indeedJob,
      isReply,
      metadata: indeedMetadata,
      candidateName,
      improvedJobPreview,
    } = params;
    const esPersonIdWithoutPlatform = extractEsPersonId(esPersonId);

    if (isReply && indeedMetadata && indeedMetadata.conversation_id && indeedMetadata.job_seeker_key) {
      await indeedReplyMessageMutation.mutateAsync({
        message,
        conversationId: indeedMetadata.conversation_id,
        jobSeekerKey: indeedMetadata.job_seeker_key,
      });
    } else {
      if (!indeedProjectId) throw TypeError('indeedProjectId must be provided');

      const isImprovedJobPreviewEnabled = isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB);

      const job: IndeedJobContact | null | undefined =
        isImprovedJobPreviewEnabled && improvedJobPreview
          ? { name: indeedJob?.name, description: improvedJobPreview }
          : indeedJob;

      await indeedSendMessageMutation.mutateAsync({
        candidateId: esPersonIdWithoutPlatform,
        senderName: currentUser.mailSignOff.fullName,
        senderCompany: currentUser.tenant.name,
        jobTitle: subject,
        message,
        projectKey: indeedProjectId,
        job,
      });

      metadata = {
        job_info: {
          jobTitle: indeedJob?.name,
        },
      };
    }

    const sanitizedName = candidateName.split(' ').join('');
    const encodedName = encodeURIComponent(sanitizedName).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    );
    const base64Name = btoa(encodedName);

    thread_id = `${currentUser.tenant.id}_${base64Name}`;

    return { thread_id, metadata: metadata as NotifyCandidateMetadata | undefined };
  });
};
